/**
 * PosSettlementAgGridHook
 *
 * AG Grid hook for the POS Settlements LiveView.
 *
 * Lifecycle
 *  mounted()  – Creates the grid with full column definitions.
 *  updated()  – Refreshes row data when the LiveView re-renders.
 *  destroyed()– Destroys the grid API to free memory.
 *
 * LiveView events received (via handleEvent):
 *  "update_pos_settlements_data" – { rows: [...] } – pushed by the server after filter/tab change.
 *
 * LiveView events pushed (via pushEvent):
 *  "pos_settlement_row_clicked" – { id: <id> } – when a row is selected.
 */

const PosSettlementAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");

    const STATUS_COLOURS = {
      settled: "#16A34A",
      rejected: "#DC2626",
      pending: "#D97706",
    };

    const SCHEME_COLOURS = {
      VISA: "#1A56DB",
      MASTERCARD: "#E3A008",
      AMEX: "#057A55",
      UNIONPAY: "#DC2626",
      DINERS: "#6B7280",
    };

    const DEFAULT_BADGE_BG = "#9CA3AF";

    const createBadge = (value, color) => {
      if (!value) return "";
      return `<span class="ag-badge" style="background:${color || DEFAULT_BADGE_BG}">${value}</span>`;
    };

    const statusBadge = (value) => createBadge(value, STATUS_COLOURS[value]);
    const schemeBadge = (value) => createBadge(value, SCHEME_COLOURS[value]);

    const amountCell = (params) => {
      if (params.value === null || params.value === undefined) return "";
      const n = parseFloat(params.value);
      return isNaN(n) ? params.value : n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const boolCell = (params) => {
      if (params.value === true)  return `<span class="ag-flag-yes">YES</span>`;
      if (params.value === false) return `<span class="ag-flag-no">No</span>`;
      return "";
    };

    const columnDefs = [
      { headerName: "#",              field: "id",                    width: 80,  pinned: "left", sortable: true, filter: false },
      { headerName: "POS Txn ID",     field: "pos_transaction_id",    width: 120 },
      { headerName: "Batch ID",       field: "settlement_batch_id",   width: 190 },
      { headerName: "Settle Date",    field: "settlement_date",       width: 120, sort: "desc" },
      { headerName: "Status",         field: "settlement_status",     width: 120, pinned: "right", cellRenderer: p => statusBadge(p.value) },
      { headerName: "Rejection Reason", field: "rejection_reason",    width: 160 },
      { headerName: "Scheme",         field: "scheme_name",           width: 120, cellRenderer: p => schemeBadge(p.value) },
      { headerName: "Txn Type",       field: "s_txn_type",            width: 100 },
      { headerName: "S TID",          field: "s_tid",                 width: 100 },
      { headerName: "S MID",          field: "s_mid",                 width: 150 },
      { headerName: "B TID",          field: "b_tid",                 width: 100 },
      { headerName: "B MID",          field: "b_mid",                 width: 150 },
      { headerName: "B TID Date",     field: "b_tid_date",            width: 110 },
      { headerName: "B TID Time",     field: "b_tid_time",            width: 100 },
      { headerName: "STAN",           field: "b_tid_stan",            width: 90  },
      { headerName: "Invoice #",      field: "b_tid_invoiceno",       width: 100 },
      { headerName: "Batch #",        field: "b_tid_batchno",         width: 100 },
      { headerName: "MTI",            field: "mti",                   width: 80  },
      { headerName: "Proc Code",      field: "proc_code",             width: 100 },
      { headerName: "Amount",         field: "total_amount",          width: 120, cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "CCY",            field: "currency_code",         width: 70  },
      { headerName: "Approval Code",  field: "approval_code",         width: 110 },
      { headerName: "Reference #",    field: "reference_no",          width: 130 },
      { headerName: "Resp Code",      field: "response_code",         width: 100 },
      { headerName: "MCC",            field: "mcc_code",              width: 80  },
      { headerName: "Masked Card",    field: "masked_card_no",        width: 160 },
      { headerName: "Acquirer ID",    field: "acquirer_id",           width: 110 },
      { headerName: "Acq Ref #",      field: "acquirer_reference_no", width: 160 },
      { headerName: "Scheme Ref #",   field: "scheme_reference_no",   width: 160 },
      { headerName: "Created DT",     field: "created_date_time",     width: 160 },
      { headerName: "Synced",         field: "synced_to_core",        width: 90, cellRenderer: boolCell },
      { headerName: "Synced At",      field: "synced_at",             width: 160 },
      { headerName: "Inserted At",    field: "inserted_at",           width: 160 },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      pagination: true,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 20, 50, 100],
      popupParent: document.body,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        minWidth: 70,
      },
      animateRows: true,
      rowSelection: "single",
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
        ],
      },
      onRowClicked: (event) => {
        if (event.data && event.data.id) {
          this.pushEvent("pos_settlement_row_clicked", { id: event.data.id });
        }
      },
      overlayNoRowsTemplate:
        `<span class="ag-no-rows-overlay">No POS settlements found. Adjust filters or check EOD batch status.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("PosSettlementAgGridHook: agGrid not found on window");
    }

    this.handleEvent("update_pos_settlements_data", ({ rows: newRows }) => {
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", newRows || []);
      }
    });
  },

  updated() {
    if (this.gridApi && this.el.dataset.rows) {
      try {
        const rows = JSON.parse(this.el.dataset.rows);
        this.gridApi.setGridOption("rowData", rows);
      } catch (e) {
        console.warn("PosSettlementAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default PosSettlementAgGridHook;
